/* global Word */
import { WordApiError } from '../../core/errors/WordApiError';
import { AppError, AppErrorCode } from '../../core/errors/AppError';
import { TableBuilder } from '../builders/TableBuilder';
import { logger } from '../../core/logging/index';
import type { TemplateProfile } from '../../templates/types';
import type { LayoutConfig } from '../../templates/types';
import { FullStrategy }         from './strategies/FullStrategy';
import { HalfStrategy }         from './strategies/HalfStrategy';
import { DoubleStrategy }       from './strategies/DoubleStrategy';
import { DoubleTableStrategy }  from './strategies/DoubleTableStrategy';
import { TableGraphStrategy }   from './strategies/TableGraphStrategy';
import { HalfWithTextStrategy } from './strategies/HalfWithTextStrategy';
import { FirstPageStrategy }    from './strategies/FirstPageStrategy';

// Strategy instances are stateless — safe to share across calls
const STRATEGIES = {
  'full':           new FullStrategy(),
  'half':           new HalfStrategy(),
  'double':         new DoubleStrategy(),
  'double-table':   new DoubleTableStrategy(),
  'table-graph':    new TableGraphStrategy(),
  'half-with-text': new HalfWithTextStrategy(),
  'first-page':     new FirstPageStrategy(),
} as const;

type StrategyKey = keyof typeof STRATEGIES;

/**
 * Inserts an exhibit table at the cursor using the active template's profile.
 *
 * Handles only table insertion. Renumbering is done by the caller (index.ts)
 * in a separate Word.run AFTER this function returns, so that all written
 * cell text is committed to the server before the renumber pass reads it.
 *
 * Call sequence inside Word.run:
 *   1. Validate profile supports requested layout
 *   2. Execute strategy  → returns inserted Word.Table
 *   3. Insert trailing paragraph so cursor exits the table
 *   4. Apply left indent (OOXML replace — MUST be last, invalidates table ref)
 */
export async function insertExhibit(
  profile: TemplateProfile,
  layout: LayoutConfig,
  context: Word.RequestContext,
  anchorRange: Word.Range,
  strategyKey: StrategyKey
): Promise<void> {
  if (!profile.supportedLayoutTypes.includes(layout.type)) {
    throw new AppError(
      `Profile "${profile.id}" does not support layout "${layout.type}"`,
      AppErrorCode.INVALID_LAYOUT_CONFIG
    );
  }

  // ── Log which profile + layout is active so issues are immediately visible ──
  // Log which profile + layout is active so issues are immediately visible ──
  logger.info('insertExhibit: starting', {
    profileId:   profile.id,
    layoutType:  layout.type,
    colWidthPt:  layout.colWidth,
    leftIndent:  (layout as any).leftIndent ?? 0,
    strategyKey,
  });

  try {
    const strategy = STRATEGIES[strategyKey];

    // Step 3 — insert table via strategy.
    // Pass 1 as a placeholder exhibit number; renumberExhibitTables (Step 5)
    // always re-scans the document in order and writes the correct final numbers.
    // Using a fixed placeholder avoids the server/local race in getNextExhibitNumber
    // where body.text may be stale after a previous Word.run on Office Online.
    const tableOrTables = await strategy.execute(
      context,
      anchorRange,
      layout,
      profile,
      1
    );

    // Step 4 — insert trailing paragraph so cursor is outside the table
    // Use the first table when a strategy returns multiple (e.g. DoubleStrategy)
    const primaryTable = Array.isArray(tableOrTables)
      ? tableOrTables[0]
      : tableOrTables;

    primaryTable.insertParagraph('', Word.InsertLocation.after);
    await context.sync();

    // Note: renumbering is intentionally NOT done here.
    // It runs in a separate Word.run in index.ts after this function returns,
    // ensuring all written cell text is visible on the server.

    // Step 4 — apply alignment or indent LAST (invalidates table reference after this)
    const leftIndent = (layout as any).leftIndent as number | undefined;
    if (leftIndent && leftIndent > 0) {
      const tables = Array.isArray(tableOrTables) ? tableOrTables : [tableOrTables];
      for (const t of tables) {
        // Natively align the table to the right margin to fit the right column layout perfectly
        t.alignment = Word.Alignment.right;
        await context.sync();
      }
    }

    logger.info('insertExhibit: complete', { profileId: profile.id, layoutType: layout.type });
  } catch (err) {
    if (err instanceof AppError) throw err;
    throw WordApiError.fromOfficeError(err, { operation: 'insertExhibit' });
  }
}